import * as React from 'react'
import { useQuery } from '@apollo/client'
import { NetworkStatus } from '@apollo/client'
import { isFinite, isNil, pickBy } from 'lodash'
import { useRouter } from 'next/router'

import { Meta } from '~/components'
import { catalogSearchQuery } from '~/gql/queries'
import { useVendor } from '~/hooks'
import { useFiltersState } from '~/hooks/use-filters-state'
import { useDynamicPageLimit } from '~/hooks/useDynamicPageLimit'
import { MainLayout } from '~/layouts'
import { createQueryUpdater, notifySentry } from '~/utils'
import type { GetMetaInfoResult } from '~/utils/get-meta-info'

import { Content } from './components/content'
import { Header } from './components/header/header'
import type { IQueryData, IQueryVariables } from './types'

interface SearchBetaScreenProps {
  metaInfo: GetMetaInfoResult
  isCategoryPage?: boolean
  page?: number
  categoryName?: string
  subcategoryName?: string
}

function SearchBetaScreen(props: SearchBetaScreenProps) {
  const { metaInfo, isCategoryPage, subcategoryName, categoryName } = props

  const router = useRouter()

  const vendor = useVendor()

  const dynamicPageLimit = useDynamicPageLimit()

  const paginationType = vendor.settings?.catalog?.products?.paginationType ?? 'infinite'

  // Read page from URL query params dynamically (updates on shallow routing)
  const currentPage = React.useMemo(() => {
    const pageParam = router.query.page
    if (typeof pageParam === 'string' && isFinite(Number(pageParam))) {
      const pageNum = Number(pageParam)

      return pageNum > 0 ? pageNum : 1
    }

    return 1
  }, [router.query.page])

  const { fixedSerializedFilters, serializedFilters, term, setTotalOfResults, sortBy } = useFiltersState()

  const queryVariables = React.useMemo(() => {
    return pickBy(
      {
        term,
        page: currentPage,
        filters: [...fixedSerializedFilters, ...serializedFilters],
        sortBy,
        ...(paginationType === 'pages' && { limit: dynamicPageLimit }),
      },
      item => !isNil(item),
    )
  }, [fixedSerializedFilters, currentPage, serializedFilters, term, sortBy, paginationType, dynamicPageLimit])

  const catalogSearchQueryResponse = useQuery<IQueryData, IQueryVariables>(catalogSearchQuery, {
    variables: queryVariables,
    onError: apolloError => notifySentry(apolloError),
  })

  const paginationInfo = catalogSearchQueryResponse.data?.catalogSearch?.pageInfo ?? ({} as IPagination)

  const products = catalogSearchQueryResponse.data?.catalogSearch?.items ?? []

  const headerTitle = subcategoryName || categoryName || `"${term}"`

  const onFetchMoreProducts = React.useCallback(
    async (page: number) => {
      await catalogSearchQueryResponse.fetchMore({
        updateQuery: createQueryUpdater(paginationType),
        variables: {
          ...queryVariables,
          page,
        },
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryVariables, paginationType],
  )

  React.useEffect(() => {
    if (!isNil(paginationInfo.total)) {
      setTotalOfResults(paginationInfo.total)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationInfo.total])

  return (
    <>
      <Meta
        title={metaInfo.title}
        image={metaInfo.image}
        url={metaInfo.url}
        description={metaInfo.description}
        asCanonical={isCategoryPage}
        prevPage={paginationInfo.hasPrevPage ? String(paginationInfo.page - 1) : null}
        nextPage={paginationInfo.hasNextPage ? String(paginationInfo.page + 1) : null}
      />
      <MainLayout>
        <Header title={headerTitle} numOfHints={paginationInfo.total} />
        <Content
          products={products}
          networkStatus={
            catalogSearchQueryResponse.loading ? NetworkStatus.loading : catalogSearchQueryResponse.networkStatus
          }
          paginationInfo={paginationInfo}
          paginationType={paginationType}
          onFetchMoreProducts={onFetchMoreProducts}
        />
      </MainLayout>
    </>
  )
}

export default SearchBetaScreen
